import { useCallback, useLayoutEffect, useState, useRef, useMemo } from 'react';
import { useEffectOnce } from 'react-use';
import { useCurrentValueGetter } from '../../../../hooks';
import config from '../../authentication/config';
import { gtmTracker, GTM_EVENTS } from '../../gtmTracker';
import { CHAT_AGENT_ACTION_TYPE } from '../config/chatAgentActionType';
import { CHAT_AGENT_COMPONENT } from '../config/chatAgentComponentGA';
import { COLLAPSED_WIDTH, EXPANDED_WIDTH } from '../config/chatAgentSideBarConfig';
import { chatClientValueStore } from '../utils/chatClientValueStore';
import { getChatAgentGAValue } from '../utils/getChatAgentGAValue';
import { useChatAgentSessionId } from './useChatAgentSessionId';

export const useChatAgentService = () => {
    const [isReady, setReady] = useState(false);
    const [isOpened, setOpen] = useState(false);
    const [isCollapsed, setIsCollapsed] = useState(true);
    const [sessionId, setSessionId, unsetSessionId] = useChatAgentSessionId();
    const [container, setContainer] = useState(null);
    const [chatRenderMode, setChatRenderMode] = useState(null);
    const [client, setClient] = useState(config.getChatAgentClient());
    chatClientValueStore.setValue(client);

    const mounted = useRef(true);
    const isConnected = useCurrentValueGetter(!!sessionId);
    const containerGetter = useCurrentValueGetter(container);
    const chatAgentWidth = useMemo(() => (isCollapsed ? COLLAPSED_WIDTH : EXPANDED_WIDTH), [isCollapsed]);

    const expand = useCallback(() => {
        setIsCollapsed(false);
    }, []);

    const collapse = useCallback(() => {
        setIsCollapsed(true);
    }, []);

    const initialize = useCallback(({ container, client = config.getChatAgentClient(), chatRenderMode } = {}) => {
        setOpen(isOpened => {
            if (!isOpened) {
                gtmTracker.trackEvent(GTM_EVENTS.USABILITY, {
                    component: CHAT_AGENT_COMPONENT,
                    value: getChatAgentGAValue({ type: CHAT_AGENT_ACTION_TYPE.OPEN_CHAT }),
                });
            }
            return true;
        });

        if (container) {
            setClient(client);
            setContainer(container);
            setChatRenderMode(chatRenderMode);
            setReady(true);
            return;
        }
    }, []);

    const open = useCallback(
        ({ container, client = config.getChatAgentClient(), chatRenderMode } = {}) => {
            if (containerGetter()) {
                expand();
                return;
            }
            initialize({ container, client, chatRenderMode });
        },
        [containerGetter, expand, initialize]
    );
    const close = useCallback(() => {
        gtmTracker.trackEvent(GTM_EVENTS.USABILITY, {
            component: CHAT_AGENT_COMPONENT,
            value: getChatAgentGAValue({ type: CHAT_AGENT_ACTION_TYPE.CLOSE_CHAT }),
        });

        collapse();
        unsetSessionId();
        setContainer(null);
        setClient(config.getChatAgentClient());
        setChatRenderMode();
        setOpen(false);
    }, [collapse, unsetSessionId]);

    useLayoutEffect(
        () => () => {
            mounted.current = false;
        },
        []
    );

    useEffectOnce(() => {
        unsetSessionId();
    });

    return {
        isOpened,
        open,
        close,
        initialize,
        container,
        isReady,
        setReady,
        client,
        sessionId,
        setSessionId,
        chatRenderMode,
        isConnected,
        isCollapsed,
        expand,
        collapse,
        chatAgentWidth,
    };
};
